import { useCallback } from 'react';
import { useToast } from 'hooks/useToast';

import * as customerApi from 'api/customerApi';
import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import {
  createCustomerForNotifications,
  CustomerForNotification,
  createCustomerForNotificationsWithTargets,
} from 'types/customer';

import {
  customersActions,
  customersSelectors,
} from 'store/reducers/notificationReducer/customersSlice';
import { listSelectors } from 'store/reducers/notificationReducer/listSlice';
import { Update } from '@reduxjs/toolkit';

/**
 * 顧客一覧 store の実装をコンポーネントから隠すための中間層
 * @returns 顧客一覧 store の selector のラッパーと action dispatch 処理 のラッパー
 */
export const useCustomerStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const loading = useAppSelector(customersSelectors.loading);
  const id = useAppSelector(customersSelectors.id);
  const name = useAppSelector(customersSelectors.name);
  const detail = useAppSelector(customersSelectors.detail);
  const total = useAppSelector(customersSelectors.total);
  const filteredCustomers = useAppSelector(
    customersSelectors.filteredCustomers,
  );
  const selectedCustomers = useAppSelector(
    customersSelectors.selectedCustomers,
  );
  const selectedCustomersLength = useAppSelector(
    customersSelectors.selectedCustomersLength,
  );
  const isSelectedAll = useAppSelector(customersSelectors.isSelectedAll);
  const isIndeterminate = useAppSelector(customersSelectors.isIndeterminate);

  // 顧客一覧をロードした際、対象顧客にチェックを入れるために利用する
  const selectedNotification = useAppSelector(
    listSelectors.selectedNotification,
  );

  const init = useCallback(() => {
    dispatch(customersActions.init());
  }, [dispatch]);

  const fetchCustomers = useCallback(async () => {
    dispatch(customersActions.loadStarted());
    const result = await customerApi.fetchCustomers();
    if (result.ok) {
      const customers = createCustomerForNotifications(result.value);
      dispatch(customersActions.fetchCustomersSuccess(customers));
    } else {
      toast({
        status: 'error',
        description: `顧客一覧の取得に失敗しました: ${result.err.message}`,
      });
    }
    dispatch(customersActions.loadEnded());
  }, [dispatch, toast]);

  const fetchCustomersAndSelectTargets = useCallback(async () => {
    if (!selectedNotification) {
      return;
    }
    const { targets } = selectedNotification;
    dispatch(customersActions.loadStarted());
    const result = await customerApi.fetchCustomers();
    if (result.ok) {
      // targets に含まれる顧客が選択された状態になった顧客一覧を生成する
      const customersWithTargets = createCustomerForNotificationsWithTargets(
        result.value,
        targets,
      );
      dispatch(customersActions.fetchCustomersSuccess(customersWithTargets));
    } else {
      toast({
        status: 'error',
        description: `顧客一覧の取得に失敗しました: ${result.err.message}`,
      });
    }
    dispatch(customersActions.loadEnded());
  }, [dispatch, selectedNotification, toast]);

  const toggleCustomer = useCallback(
    (customerId: number, nextCheckState: boolean) => {
      dispatch(
        customersActions.customerUpdated({
          id: customerId,
          changes: { isSelected: nextCheckState },
        }),
      );
    },
    [dispatch],
  );

  const toggleAllCustomers = useCallback(() => {
    const nextCheckState = !isSelectedAll;
    const updates: Update<CustomerForNotification>[] = filteredCustomers.map(
      (customer) => ({
        id: customer.id,
        changes: {
          isSelected: nextCheckState,
        },
      }),
    );
    dispatch(customersActions.customersUpdated(updates));
  }, [dispatch, filteredCustomers, isSelectedAll]);

  const changeId = useCallback(
    (customerId: string) => {
      dispatch(
        customersActions.idChanged({
          id: customerId,
        }),
      );
    },
    [dispatch],
  );

  const changeName = useCallback(
    (_name: string) => {
      dispatch(
        customersActions.nameChanged({
          name: _name,
        }),
      );
    },
    [dispatch],
  );

  const changeDetail = useCallback(
    (_detail: string) => {
      dispatch(
        customersActions.detailChanged({
          detail: _detail,
        }),
      );
    },
    [dispatch],
  );

  return {
    loading,
    id,
    name,
    detail,
    total,
    filteredCustomers,
    selectedCustomers,
    selectedCustomersLength,
    isSelectedAll,
    isIndeterminate,
    init,
    fetchCustomers,
    fetchCustomersAndSelectTargets,
    toggleCustomer,
    toggleAllCustomers,
    changeId,
    changeName,
    changeDetail,
  };
};
